import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import {
  CustomDataTable,
  TableColumn,
} from "../../components/CommnTable/CustomDataTable";
import { useSearchParams } from "react-router-dom";
import { configWithToken, GetUrl } from "../../api/endpointConfig";
import { useGet } from "../../services/hooks/useGet";
import { useLoader } from "../../Loader/LoaderContext";
import { useDispatch } from "react-redux";
import {
  ScholarshipReportType,
  ScholarshipSummaryFilters,
} from "../../types/types";
import axios from "axios";
import { API_URL, API_VERSION } from "../../config/config";
import StatCard from "../../components/StatCard/StatCard";
import styles from "./ManagementReports.module.scss";
import { head } from "lodash";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import dayjs from "dayjs";
import { apiClient } from "../../services/hooks/api";
const generateColor = (index: number) => {
  const palette = [
    "#3366CC",
    "#DC3912",
    "#FF9900",
    "#109618",
    "#990099",
    "#0099C6",
    "#DD4477",
    "#66AA00",
    "#B82E2E",
    "#316395",
  ];
  return palette[index % palette.length];
};
const ManagementReports: React.FC = () => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const [offsetValue, setOffsetValue] = useState<number>(0);
  const [limitValue, setLimitValue] = useState<number>(20);
  const [durationType, setDurationType] = useState<string>("monthly");
  const [mainData, setMainData] = useState<ScholarshipReportType[]>([]);
  const [filteredData, setFilteredData] = useState<ScholarshipReportType[]>([]);
  const [mainSpentData, setMainSpentData] = useState<
    ScholarshipSummaryFilters[]
  >([]);
  const [mainUserGrothData, setMainUserGrothData] = useState<any[]>([]);
  const [filterSpentData, setfilterSpentData] = useState<
    ScholarshipSummaryFilters[]
  >([]);

  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedForcastYear, setSelectedForcastYear] = useState<string | null>(
    String(new Date().getFullYear()),
  );
  const [selectedFyYear, setSelectedFyYear] = useState<string | null>(
    (() => {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
      return `${startYear}-${startYear + 1}`;
    })(),
  );
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>({});
  const { type } = useParams();
  const pagination: Record<string, string | number | null> = {
    offset: 0,
    limit: 0,
  };
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentMonthName = monthNames[new Date().getMonth()];
  const [forecastValue, setForecastValue] = useState<string>(currentMonthName);

  // Fiscal year: April–March
  const fiscalMonths = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const fiscalQuarters = [
    { label: "Q1 (Apr–Jun)", value: "Q1" },
    { label: "Q2 (Jul–Sep)", value: "Q2" },
    { label: "Q3 (Oct–Dec)", value: "Q3" },
    { label: "Q4 (Jan–Mar)", value: "Q4" },
  ];

  const today = new Date();
  const currentMonth = today.getMonth(); // 0–11
  const currentYear = today.getFullYear();

  // Compute current fiscal index
  const fiscalStartIndex = 3; // April (index 3 in normal month array)
  const monthIndexInFiscalYear = (currentMonth - fiscalStartIndex + 12) % 12;
  const currentQuarterIndex = Math.floor(monthIndexInFiscalYear / 3);
  const currentFiscalYearStart =
    currentMonth < 3 ? currentYear - 1 : currentYear;

  const years: string[] = Array.from({ length: 11 }, (_, i) =>
    String(currentYear - 5 + i),
  );

  // Generate fiscal years (FY2024–25 format)
  const generateFiscalYears = () => {
    const startYear = currentMonth < 3 ? currentYear - 1 : currentYear; // if before April, FY starts previous year
    return Array.from({ length: 6 }, (_, i) => {
      const fyStart = startYear + i;
      const fyEnd = fyStart + 1;
      const obj = {
        label: `FY ${fyStart}-${fyEnd.toString().slice(-2)}`,
        value: `${fyStart}-${fyEnd.toString().slice(-2)}`,
      };
      return obj;
    });
  };

  const generateFiscalFullYears = () => {
    const startYear = currentMonth < 3 ? currentYear - 1 : currentYear; // if before April, FY starts previous year
    return Array.from({ length: 6 }, (_, i) => {
      const fyStart = startYear - 5 + i;
      const fyEnd = fyStart + 1;
      const obj = {
        label: `FY ${fyStart}-${fyEnd}`,
        value: `${fyStart}-${fyEnd}`,
      };
      return obj;
    });
  };

  // Filter months and quarters dynamically (current → forward)
  const getFilteredOptions = () => {
    const selectedYearNum = Number(selectedForcastYear);
    const isCurrentFiscalYear = selectedYearNum === currentFiscalYearStart;
    switch (durationType) {
      case "monthly":
        return isCurrentFiscalYear
          ? fiscalMonths.slice(monthIndexInFiscalYear)
          : fiscalMonths;
      case "quarterly":
        return fiscalQuarters.slice(currentQuarterIndex);
      case "yearly":
        return generateFiscalYears();
      default:
        return [];
    }
  };

  const handleForecastSelection = (value: string) => {
    setForecastValue(value);
  };

  const renderForecastDropdown = () => {
    const options = getFilteredOptions();
    if (!durationType) return null;

    let label = "";
    if (durationType === "monthly") label = "Select Month";
    if (durationType === "quarterly") label = "Select Quarter";
    if (durationType === "yearly") label = "Select Fiscal Year";

    return (
      <TextField
        fullWidth
        select
        label={label}
        value={forecastValue}
        onChange={(e) => handleForecastSelection(e.target.value)}
        variant="outlined"
        size="small"
        style={{ maxWidth: "20rem" }}
      >
        {options.map((option: any) => {
          return (
            <MenuItem key={option} value={option.value ? option.value : option}>
              {option.label ? option.label : option}
            </MenuItem>
          );
        })}
      </TextField>
    );
  };

  useEffect(() => {
    const prefix = searchParams.get("prefix");
    const text = searchParams.get("text");
    setQueryParams((prev: any) => {
      const updated = { ...prev };

      if (text && prefix) {
        updated[prefix] = text;
      } else {
        setQueryParams({});
      }
      return updated;
    });
  }, [searchParams]);
  const setClearFilterState = (state: boolean) => {
    console.log("Clear filter state in parent:", state);
    if (state) {
      setQueryParams({});
    }
  };

  const fetchReport = async (
    durationType: string,
    forecastValue: string,
    selectedFYear: any,
  ) => {
    if (!durationType || !forecastValue) {
      console.warn("Duration type or forecast value not selected yet.");
      return null;
    }

    try {
      return await apiClient.get(GetUrl.getReport, {
        params: {
          report_type: durationType,
          report_duration: forecastValue,
          report_year: selectedFYear,
        },
      });
    } catch (error) {
      console.error("Error fetching report:", error);
      throw error;
    }
  };

  const fetchSpentReport = async (
    durationType: string,
    forecastValue: string,
  ) => {
    if (!durationType || !forecastValue) {
      console.warn("Duration type or forecast value not selected yet.");
      return null;
    }

    try {
      return await apiClient.get(GetUrl.getSpentReport, {
        params: {
          financial_year: selectedFyYear,
        },
      });
    } catch (error) {
      console.error("Error fetching report:", error);
      throw error;
    }
  };

  const fetchUserGrowthReport = async (
    durationType: string,
    forecastValue: string,
  ) => {
    if (!durationType || !forecastValue) {
      console.warn("Duration type or forecast value not selected yet.");
      return null;
    }

    try {
      return await apiClient.get(GetUrl.getUserGrowthReport, {
        params: {
          ...queryParams,
        },
      });
    } catch (error) {
      console.error("Error fetching report:", error);
      throw error;
    }
  };

  useEffect(() => {
    const getData = async () => {
      if (
        durationType &&
        forecastValue &&
        selectedForcastYear &&
        type === "forecastReports"
      ) {
        showLoader();
        try {
          const result = await fetchReport(
            durationType,
            forecastValue,
            selectedForcastYear,
          );

          setMainData(result || []);
        } catch (err) {
          setMainData([]);
        } finally {
          hideLoader();
        }
      } else if (type === "allReports") {
        showLoader();
        try {
          const result = await fetchSpentReport(durationType, forecastValue);
          setMainSpentData(result || []);
        } catch (err) {
          setMainSpentData([]);
        } finally {
          hideLoader();
        }
      } else if (type === "userGrowthReports") {
        showLoader();
        try {
          const result = await fetchUserGrowthReport(
            durationType,
            forecastValue,
          );
          console.log("Fetched User Growth Data:", result);

          setMainUserGrothData([result]);
        } catch (err) {
          setMainUserGrothData([]);
        } finally {
          hideLoader();
        }
      }
      console.log("Fetched queryParams:", queryParams);
    };

    getData();
  }, [durationType, forecastValue, type, selectedFyYear]);

  useEffect(() => {
    if (type === "forecastReports") {
      const filtered = mainData.filter((item) =>
        Object.entries(queryParams).every(([key, value]) => {
          if (!value) return true;
          const itemValue = String(
            item[key as keyof ScholarshipReportType] || "",
          ).toLowerCase();
          return itemValue.includes(value.toLowerCase());
        }),
      );
      setFilteredData(filtered);
    } else if (type === "allReports") {
      const filtered = mainSpentData.filter((item) =>
        Object.entries(queryParams).every(([key, value]) => {
          if (!value) return true;
          const itemValue = String(
            item[key as keyof ScholarshipSummaryFilters] || "",
          ).toLowerCase();
          return itemValue.includes(value.toLowerCase());
        }),
      );
      setfilterSpentData(filtered);
    } else if (type === "userGrowthReports") {
      // No filtering logic for user growth reports as of now
    }
  }, [queryParams, mainData, mainSpentData, mainUserGrothData, type]);

  const yearData = useMemo(() => {
    const mapped = mainUserGrothData.flatMap((item, idx) => {
      return Object.keys(item).map((year, innerIdx) => ({
        year,
        total: item[year].total_count,
        color: generateColor(idx + innerIdx),
      }));
    });

    return mapped;
  }, [mainUserGrothData]);

  const monthData = useMemo(() => {
    if (!selectedYear) return [];

    const found = mainUserGrothData.find((x) => x[selectedYear]);

    if (!found) return [];

    const months = found[selectedYear].monthly;

    const mapped = Object.keys(months).map((m, i) => ({
      month: m,
      value: months[m],
      color: generateColor(i),
    }));

    return mapped;
  }, [selectedYear, mainUserGrothData]);

  const columns: TableColumn<ScholarshipReportType>[] = [
    {
      field: "scholarship_id",
      header: "Scholarship ID",
      width: "10rem",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      filterable: true,
      // customBody: (rowData) => (
      //   <span
      //     style={{
      //       color: "#1976d2",
      //       cursor: "pointer",
      //       textDecoration: "underline",
      //     }}
      //     onClick={() => {

      //       navigate(`/scholarship-summary/${rowData.scholarship_id}`);
      //     }}
      //   >
      //     {rowData.scholarship_id}
      //   </span>
      // ),
    },
    {
      field: "Name",
      header: "Name",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap", textTransform: "capitalize" },
    },
    {
      field: "course",
      header: "Course",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "Stream",
      header: "Stream",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "next_installment_dt",
      header: "Next Installment Date",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
      customBody: (rowData) => {
        const onlyDate = dayjs(rowData.next_installment_dt).format(
          "YYYY-MM-DD",
        );
        return onlyDate;
      },
    },
    {
      field: "next_installment_purpose",
      header: "Next Installment Purpose",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "next_installment_amt",
      header: "Next Installment Amount",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "account_holder_name",
      header: "Account Holder Name",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "account_no",
      header: "Account No",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "IFSC_no",
      header: "IFSC No",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "branch",
      header: "Branch",
      filterable: true,
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "bank_name",
      header: "Bank Name",
      sortable: true,
      style: { whiteSpace: "nowrap" },
    },
  ];
  const columnsSpent: TableColumn<ScholarshipSummaryFilters>[] = [
    {
      field: "payment_date",
      header: "Payment Date",
      width: "10rem",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      filterable: true,
    },
    {
      field: "cheque_clearing_date",
      header: "Cheque Clearing Date",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap", textTransform: "capitalize" },
    },
    {
      field: "course",
      header: "Course",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "school_or_institute",
      header: "School or Institute",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "paid_to",
      header: "Paid To",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "transaction_number",
      header: "Transaction Number",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "cheque_number",
      header: "Cheque Number",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "dd_number",
      header: "DD Number",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "amount",
      header: "Account",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      field: "purpose",
      header: "Purpose",
      sortable: true,
      filterable: true,
      style: { whiteSpace: "nowrap" },
    },
  ];

  const handlePageChange = (event: any) => {
    setOffsetValue(event.page * event.rows);

    setLimitValue(event.rows);
  };
  return (
    <>
      <Box className={`${styles.section}`}>
        <Box className={styles.statCardWrapper}>
          <StatCard
            cardId="forecastReports"
            title="Payment Forecast Reports"
            value={""}
            color="#fff"
            icon={<PaymentsIcon />}
            link=""
          />
        </Box>
        <Box className={styles.statCardWrapper}>
          <StatCard
            cardId="allReports"
            title="Auditor Reports"
            value={""}
            color="#fff"
            icon={<ReceiptIcon />}
            link=""
          />
        </Box>
        <Box className={styles.statCardWrapper}>
          <StatCard
            cardId="userGrowthReports"
            title="User Growth Reports"
            value={""}
            color="#fff"
            icon={<PaymentsIcon />}
            link=""
          />
        </Box>
      </Box>
      {type == "forecastReports" && (
        <Card
          style={{
            padding: "1rem",
            border: 0,
            boxShadow: "none",
            background: "none",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginBottom: "1rem",
              gap: "1rem",
            }}
          >
            <TextField
              fullWidth
              select
              id="yearId"
              name="Year"
              label="Select Year"
              value={selectedForcastYear}
              onChange={(e) => {
                setSelectedForcastYear(e.target.value);
                setDurationType("");
                setForecastValue("");
              }}
              variant="outlined"
              size="small"
              style={{ maxWidth: "15rem" }}
            >
              <MenuItem value="">Select Year</MenuItem>
              {years.map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              id={`typeid`}
              name={`DurationType`}
              label={`Duration Type`}
              value={durationType}
              onChange={(e) => {
                setDurationType(e.target.value);
                setForecastValue("");
              }}
              variant="outlined"
              size="small"
              style={{ maxWidth: "20rem" }}
            >
              <MenuItem value="">Select Type</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              {/* <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem> */}
            </TextField>
            {renderForecastDropdown()}
          </Box>
          <Box className="cardBoxUI cardBoxUIReport">
            <CustomDataTable<ScholarshipReportType>
              data={filteredData}
              columns={columns}
              tableTitle="Payment Forecast Reports"
              rowsPerPage={limitValue}
              first={offsetValue}
              rows={limitValue}
              exportCSVShow={false}
              exportExcelShow={true}
              onSearchClear={() => setQueryParams({})}
              onPageChange={handlePageChange}
              clearFilter={setClearFilterState}
            />
          </Box>
        </Card>
      )}
      {type == "allReports" && (
        <>
          <Card
            style={{
              padding: "1rem",
              border: 0,
              boxShadow: "none",
              background: "none",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                marginBottom: "1rem",
                gap: "1rem",
              }}
            >
              <TextField
                fullWidth
                select
                label="Select Fiscal Year"
                value={selectedFyYear}
                onChange={(e) => setSelectedFyYear(e.target.value)}
                variant="outlined"
                size="small"
                style={{ maxWidth: "20rem" }}
              >
                {generateFiscalFullYears().map((fy) => (
                  <MenuItem key={fy.value} value={fy.value}>
                    {fy.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box className="cardBoxUI cardBoxUIReport">
              <CustomDataTable<ScholarshipSummaryFilters>
                data={filterSpentData}
                columns={columnsSpent}
                tableTitle="Auditor Reports"
                rowsPerPage={limitValue}
                first={offsetValue}
                rows={limitValue}
                exportCSVShow={false}
                exportExcelShow={true}
                onSearchClear={() => setQueryParams({})}
                onPageChange={handlePageChange}
                clearFilter={setClearFilterState}
              />
            </Box>
          </Card>
        </>
      )}
      {type == "userGrowthReports" && (
        <>
          <Card
            style={{
              padding: "1rem",
              border: 0,
              boxShadow: "none",
              background: "none",
            }}
          >
            <Box className="cardBoxUI cardBoxUIReport">
              <Box
                mb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" sx={{ fontWeight: "600" }}>
                  User Growth Reports
                </Typography>
              </Box>
              <Box style={{ height: 400, width: "100%" }}>
                {/* YEAR VIEW */}
                {!selectedYear && (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={yearData}
                      layout="horizontal"
                      margin={{ left: 80, right: 20 }}
                      barSize={40}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="category" dataKey="year" />
                      <YAxis type="number" />
                      <Tooltip />

                      <Bar dataKey="total">
                        {yearData.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={entry.color}
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedYear(entry.year)} // 🔥 100% reliable click
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {/* MONTH VIEW */}
                {selectedYear && (
                  <Box style={{ width: "100%", height: "100%" }}>
                    <button
                      onClick={() => setSelectedYear(null)}
                      style={{
                        marginBottom: 10,
                        padding: "6px 12px",
                        background: "#e0e0e0",
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Back to Yearly
                    </button>

                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart
                        data={monthData}
                        layout="horizontal"
                        margin={{ left: 80, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="category" dataKey="month" />
                        <YAxis type="number" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3366CC" barSize={40}>
                          {monthData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </>
      )}
    </>
  );
};

export default ManagementReports;
